import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { echartsContent, transChannel } from '@op/utils';
import { DBO_EC } from '@op/utils/format';
import { chartAllColors, NO_DATE } from '@op/constants';
import { useIntl } from 'umi';
import { getXAxisAxisLabel } from '@op/utils/chart';

interface IMetricChart {
  height?: number;
  xAxisData: Array<string>;
  seriesData: Array<any>;
  legendData?: Array<string>;
  title?: string;
  formatValueFunc?: (value: number | string) => string | number;
  filter: any;
  rightTitle?: string;
  chartType?: string;
}
export default function MetricChart(props: IMetricChart) {
  const {
    height = 75,
    xAxisData,
    seriesData,
    legendData = [],
    title: customTitle,
    rightTitle = '',
    formatValueFunc,
    filter,
    chartType = 'line',
  } = props;
  const { date_type: dateType } = filter || {};
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const series =
      seriesData?.map((val, index) => {
        const color = chartAllColors[index];
        const { name, data } = val;
        return {
          name,
          type: chartType,
          data,
          areaStyle: DBO_EC.gradientColor(color, 0, 0.4),
          lineStyle: {
            type: index === 0 ? 'solid' : 'dashed',
          },
        };
      }) || [];

    const option = {
      tooltip: {
        appendToBody: true,
        position() {
          return { top: -80, right: -10 };
        },
        confine: false,
        formatter: (param: any) => {
          const { axisValue } = param?.[0] || {};
          const title = customTitle || listTimeTitleFunc(axisValue, dateType);
          return formatChartParamsDate({
            params: param,
            listTitleFunc: (series: any) => {
              return formatMessage({ id: series.seriesName || NO_DATE.EMPTY });
            },
            valueFormat: formatValueFunc,
            // textShowFun: (marker, _seriesName, data) =>
            //   `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;${formatMessage(
            //     { id: data?.seriesName || _seriesName },
            //   )}`,
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: title,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: rightTitle,
                  },
                ],
              },
            ],
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (params: any) => transChannel(params),
      },
      grid: {
        top: '8%',
        left: '5%',
        right: '12%',
        containLabel: false,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: 0,
          data: xAxisData,
          axisTick: false,
          splitLine: false,
          axisLabel: getXAxisAxisLabel(filter),
        },
      ],
      yAxis: {
        type: 'value',
        splitLine: {
          show: true,
        },
        splitNumber: 1.5,
        boundaryGap: 0,
        position: 'right',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
        },
        max(value: any) {
          if (!isNaN(value.max)) {
            return Math.ceil(value.max / 20) * 20;
          }
          return 20;
        },
        min: 0,
      },
      series,
    };

    return option;
  }, [
    formatMessage,
    chartType,
    customTitle,
    dateType,
    formatValueFunc,
    legendData,
    rightTitle,
    seriesData,
    xAxisData,
  ]);

  return (
    <EChartCommon
      options={options}
      height={height}
      optionsProps={{ excludeOptions: ['axisPointer'] }}
    ></EChartCommon>
  );
}
